import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

export default function RequestHardwareQuote() {
  const canonical = 'https://divaisetech.com/request-hardware-quote';
  const servicePageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'IT Hardware Quote Request Service',
    description: 'Request and receive a custom enterprise IT hardware procurement quote and consultation.',
    url: canonical,
    provider: {
      '@type': 'Organization',
      name: 'DivaiseTech',
      url: 'https://divaisetech.com',
      logo: 'https://divaisetech.com/logo.png',
    },
    areaServed: {
      '@type': 'Place',
      name: 'Worldwide',
    },
    serviceType: 'Hardware Procurement Quotation',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
    },
  };

  const hardwareQuoteSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'IT Hardware Quote Request',
    description: 'Request a custom quote for enterprise IT hardware procurement. Free consultation, no obligation.',
    url: canonical,
    serviceType: 'Enterprise IT Hardware Procurement Quote',
    provider: {
      '@type': 'Organization',
      name: 'Divaise Technology Private Limited',
      url: 'https://divaisetech.com',
      email: 'consult@divaisetech.com',
      telephone: '+91-8287934973',
    },
    areaServed: {
      '@type': 'Place',
      name: 'Worldwide',
    },
  };
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    hardwareType: '',
    quantity: '',
    budget: '',
    timeline: '',
    requirements: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In production, this would submit to a backend/email service
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <div>
      <SEO
        title="Get IT Hardware Quote | Procurement Services"
        description="Request a custom quote for enterprise IT hardware procurement. Free consultation, no obligation."
        keywords="hardware quote, IT procurement, purchase request, server quote, laptop procurement"
        canonical={canonical}
        schema={[hardwareQuoteSchema, servicePageSchema]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Request Your Hardware Quote</h1>
          <p className="text-xl text-slate-200 mb-8">
            Fill out a simple form and get a custom, no-obligation hardware quote from our procurement experts within 24 hours.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-2xl mx-auto">
          {!submitted ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Company & Contact Info */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Company Name *</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                    placeholder="Your company name"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                      placeholder="your@company.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                    placeholder="+91 98765 43210"
                  />
                </div>

                {/* Hardware Details */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">What hardware are you interested in? *</label>
                  <select
                    name="hardwareType"
                    value={formData.hardwareType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  >
                    <option value="">Select hardware type</option>
                    <option value="gpu-servers">GPU Servers & AI Hardware</option>
                    <option value="enterprise-servers">Enterprise Servers</option>
                    <option value="storage">Storage Systems</option>
                    <option value="networking">Networking Equipment</option>
                    <option value="laptops">Laptops & Workstations</option>
                    <option value="printers">Printers & Multifunction Devices</option>
                    <option value="cctv">CCTV & Security Systems</option>
                    <option value="mixed">Mixed/Multiple Categories</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Quantity *</label>
                    <input
                      type="number"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                      placeholder="e.g., 50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Budget Range *</label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                    >
                      <option value="">Select budget</option>
                      <option value="under-500k">Under $500K</option>
                      <option value="500k-1m">$500K - $1M</option>
                      <option value="1m-5m">$1M - $5M</option>
                      <option value="5m-plus">$5M+</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Timeline Needed *</label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  >
                    <option value="">Select timeline</option>
                    <option value="immediate">Immediate (2-4 weeks)</option>
                    <option value="short">Short-term (1-3 months)</option>
                    <option value="medium">Medium-term (3-6 months)</option>
                    <option value="long">Long-term (6+ months)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Additional Requirements</label>
                  <textarea
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleChange}
                    rows="5"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                    placeholder="Specific hardware models, performance requirements, integration needs, or other details..."
                  ></textarea>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full bg-accent text-black px-6 py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all inline-flex items-center justify-center gap-2"
                >
                  Get My Free Quote <ArrowRight className="w-5 h-5" />
                </button>

                <p className="text-center text-sm text-gray-600">
                  We'll review your request and send a detailed quote within 24 business hours. No credit card required.
                </p>
              </form>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-xl p-12 text-center">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Request Received!</h2>
              <p className="text-lg text-gray-700 mb-6">
                Thank you for your hardware quote request. Our procurement team will review your requirements and send you a custom quote within 24 hours.
              </p>
              <p className="text-gray-600 mb-8">
                Watch your email for updates. In the meantime, you can contact us directly at <a href="mailto:consult@divaisetech.com" className="text-accent font-semibold">consult@divaisetech.com</a> or <a href="tel:+918287934973" className="text-accent font-semibold">+91-8287934973</a>.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center bg-accent text-black px-6 py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all"
              >
                Contact Our Team <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-white">Why Request Hardware Through Divaise?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Best Pricing',
                desc: 'Direct vendor relationships give us access to competitive pricing and volume discounts.',
              },
              {
                title: 'Expert Sourcing',
                desc: 'Our team recommends optimal hardware configurations for your specific needs.',
              },
              {
                title: 'Fast Delivery',
                desc: 'Global fulfillment network ensures quick delivery to any location worldwide.',
              },
              {
                title: 'Full Integration',
                desc: 'Installation, configuration, and testing services include with hardware delivery.',
              },
              {
                title: 'Warranty Support',
                desc: 'We negotiate extended warranties and arrange on-site support contracts.',
              },
              {
                title: '24/7 Support',
                desc: 'Dedicated account management and round-the-clock technical support included.',
              },
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <CheckCircle className="w-8 h-8 text-accent mx-auto mb-4" />
                <h3 className="font-bold text-white mb-2">{item.title}</h3>
                <p className="text-slate-300 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
